'use client'

/**
 * Purpose: Hook that posts a captured selfie to `/api/face-analysis` during the survey photo flow.
 * Inputs: base64 image, the research-consent opt-in, and stage/error setters from SurveyCameraCapture.
 * Outputs: Callback that drives analyzing → success/error stage transitions and hands a
 *   successful response back to the caller via onSuccess.
 */

import { useCallback } from 'react'
import { useTranslations } from 'next-intl'
import { callFaceAnalysis, RateLimitedError } from '@lib/face-analysis-client'
import { logger } from '@lib/logger'
import type { FaceAnalysisResponse } from '@lib/models/face-api'
import { isFaceImageBase64WithinLimit } from '@lib/image-limits'

type Stage =
  | 'choose'
  | 'analyzing'
  | 'noFace'
  | 'multipleFaces'
  | 'imageBlurry'
  | 'imageUnderexposed'
  | 'unsafeContent'
  | 'rateLimited'
  | 'visionUnavailable'
  | 'error'

/** Callbacks and stage setters wired from SurveyCameraCapture. */
type Options = {
  /**
   * Called with a face-found response. The caller decides what happens next —
   * this hook owns the request and its error mapping, not the flow after success.
   */
  onSuccess: (result: FaceAnalysisResponse) => void
  /** Optional research opt-in from the consent screen; forwarded to the API. */
  researchConsent: boolean
  setStage: (stage: Stage) => void
  setCameraError: (message: string | null) => void
  setNotConfigured: (value: boolean) => void
  setRetryAfter: (value: string | null) => void
}

export function useSurveyFaceAnalysis({
  onSuccess,
  researchConsent,
  setStage,
  setCameraError,
  setNotConfigured,
  setRetryAfter,
}: Options) {
  const t = useTranslations('survey.photo')

  return useCallback(
    async (base64: string) => {
      // Guard empty payloads before hitting the API.
      if (!base64) {
        setStage('error')
        return
      }
      // Client-side size check — same limit as the face-analysis route.
      if (!isFaceImageBase64WithinLimit(base64)) {
        setCameraError(t('imageTooLarge'))
        setStage('choose')
        return
      }
      // Show analyzing stage while the face-analysis route runs.
      setStage('analyzing')
      try {
        const result = await callFaceAnalysis(base64, researchConsent)
        // No face detected — let the user retake without treating it as a server error.
        if (!result.faceFound) {
          setStage('noFace')
          return
        }
        onSuccess(result)
      } catch (err) {
        // Dedicated stage: hide retry, show quota reset time + questionnaire CTA.
        if (err instanceof RateLimitedError) {
          setRetryAfter(err.retryAfter)
          setStage('rateLimited')
          return
        }
        const msg = err instanceof Error ? err.message : String(err)
        logger.error('face-analysis', 'request failed', err)
        // Map known API error codes to dedicated UI stages, each with a specific retake reason.
        if (msg === 'not_configured') {
          setNotConfigured(true)
          setStage('error')
        } else if (msg === 'vision_unavailable') {
          // Provider outage — questionnaire CTA, distinct from a generic pipeline error.
          setStage('visionUnavailable')
        } else if (msg === 'multiple_faces') {
          setStage('multipleFaces')
        } else if (msg === 'image_blurry') {
          setStage('imageBlurry')
        } else if (msg === 'image_underexposed') {
          setStage('imageUnderexposed')
        } else if (msg === 'unsafe_content') {
          setStage('unsafeContent')
        } else {
          // Unknown failures fall back to generic error with retry.
          setStage('error')
        }
      }
    },
    [onSuccess, researchConsent, setCameraError, setNotConfigured, setRetryAfter, setStage, t],
  )
}
